import { HOME_STATS_DAYS, type HomeStats as HomeStatsData } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Card, CardBody, CardHeader, CardTitle, EmptyState, ErrorState, Skeleton } from "@sagot/ui";
import { useHomeStats } from "./use-home-data";

const K = "deployKnowledge.widgetStudio.home.stats";

interface HomeStatsProps {
  agentId: string;
  /** Fetch only once the Home tab is open. */
  active: boolean;
  /** Option labels by id (taps are counted per option id). */
  labels: ReadonlyMap<string, string>;
}

function StatsSkeleton() {
  return (
    <div aria-hidden="true" data-testid="home-stats-loading" className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
      <Skeleton className="h-4 w-[160px]" />
      <Skeleton className="h-8 w-full" />
      <Skeleton className="h-8 w-full" />
    </div>
  );
}

function Figure({ id, label, value }: { id: string; label: string; value: number }) {
  const { i18n } = useTranslation();
  return (
    <div className="rounded-lg border border-line p-3">
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd className="mt-1 font-mono text-2xl font-semibold" data-testid={`home-stat-${id}`}>
        {new Intl.NumberFormat(i18n.language).format(value)}
      </dd>
    </div>
  );
}

function Numbers({ data, labels }: { data: HomeStatsData; labels: ReadonlyMap<string, string> }) {
  const { t } = useTranslation();
  const ids = [...new Set([...labels.keys(), ...Object.keys(data.taps)])];
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-3">
        <Figure id="impressions" label={t(`${K}.impressions`)} value={data.impressions} />
        <Figure id="conversations" label={t(`${K}.conversations`)} value={data.conversations} />
      </dl>
      <div>
        <h3 className="text-xs font-bold text-fg-subtle">{t(`${K}.taps`)}</h3>
        {ids.length === 0 ? (
          <p className="mt-2 text-xs text-fg-subtle">{t(`${K}.noTaps`)}</p>
        ) : (
          <ul className="mt-2 divide-y divide-line">
            {ids.map((id) => (
              <li key={id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="min-w-0 truncate">{labels.get(id) ?? t(`${K}.removedOption`, { id })}</span>
                <span className="shrink-0 font-mono text-xs text-fg-muted">{t(`${K}.tapCount`, { count: data.taps[id] ?? 0 })}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** The Home tab's analytics footer: views, taps per option and conversations from Home. */
export function HomeStats({ agentId, active, labels }: HomeStatsProps) {
  const { t } = useTranslation();
  const stats = useHomeStats(agentId, active);
  const data = stats.data;
  const unused = data ? isUnused(data) : false;
  return (
    <Card aria-labelledby="home-stats-title" data-testid="home-stats">
      <CardHeader>
        <CardTitle id="home-stats-title">{t(`${K}.title`)}</CardTitle>
        <span className="text-xs text-fg-subtle">{t(`${K}.window`, { days: HOME_STATS_DAYS })}</span>
      </CardHeader>
      <CardBody>
        {stats.isError ? (
          <ErrorState headingLevel={3} title={t(`${K}.errorTitle`)} description={t(`${K}.errorBody`)} retryLabel={t("common.retry")} onRetry={() => void stats.refetch()} />
        ) : data === undefined ? (
          <StatsSkeleton />
        ) : unused ? (
          <EmptyState icon="bar-chart" headingLevel={3} title={t(`${K}.emptyTitle`)} description={t(`${K}.emptyBody`)} />
        ) : (
          <Numbers data={data} labels={labels} />
        )}
      </CardBody>
    </Card>
  );
}

const isUnused = (data: HomeStatsData): boolean => data.impressions === 0 && data.conversations === 0 && Object.keys(data.taps).length === 0;
